import { useRef, useEffect, useMemo } from "react";
import { usePanPinchZoom } from "./usePanPinchZoom";
import { ZoomControl } from "./ZoomControl";
import {
  ZoomStaticButtonContainer,
  ZoomableImageContainer,
  StyledZoomableImage,
} from "../styles/styles";

interface ZoomableImageProps {
  src: string;
  alt?: string;
  rotation?: number;
  fallback?: boolean;
  showZoomControls?: boolean;
  initialZoom?: number;
  minZoom?: number;
  maxZoom?: number;
  zoomStep?: number;
}

const DOUBLE_TAP_THRESHOLD_MS = 300;

const getDistance = (touch1: Touch, touch2: Touch) => {
  const dx = touch1.clientX - touch2.clientX;
  const dy = touch1.clientY - touch2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
};

const getTwoTouches = (
  touchList: TouchList
): { touch1: Touch; touch2: Touch } | null => {
  if (touchList.length !== 2) return null;

  const touch1 = touchList[0];
  const touch2 = touchList[1];

  if (!touch1 || !touch2) return null;

  return { touch1, touch2 };
};

export const ZoomableImage = ({
  src,
  alt,
  rotation = 0,
  fallback = false,
  showZoomControls = true,
  initialZoom = 100,
  minZoom = 100,
  maxZoom = 500,
  zoomStep = 100,
}: ZoomableImageProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastTapTimeRef = useRef(0);

  const {
    zoomLevel,
    zoomScale,
    isZoomed,
    panPosition,
    isDragging,
    zoomIn,
    zoomOut,
    startDrag,
    updateDrag,
    endDrag,
    startPinch,
    updatePinch,
    endPinch,
    resetZoom,
  } = usePanPinchZoom({
    initialZoom,
    minZoom,
    maxZoom,
    zoomStep,
    rotation,
  });

  const inlineStyle = useMemo(
    () => ({
      cursor: isZoomed ? (isDragging ? "grabbing" : "grab") : "default",
      touchAction: isZoomed ? "none" : "auto",
    }),
    [isZoomed, isDragging]
  );

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isZoomed) return;
    e.preventDefault();
    startDrag(e.clientX, e.clientY);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    updateDrag(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    endDrag();
  };

  // Native touch event handlers for mobile (to prevent passive event listener issues)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let previousTouchCount = 0;

    const handleTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("button")) return;

      previousTouchCount = e.touches.length;

      // Handle pinch zoom
      if (e.touches.length === 2) {
        const touches = getTwoTouches(e.touches);
        if (!touches) return;

        e.preventDefault();
        e.stopPropagation();
        const distance = getDistance(touches.touch1, touches.touch2);
        startPinch(distance);
        return;
      }

      // Handle double tap to reset zoom (1 finger)
      if (e.touches.length === 1) {
        const now = Date.now();
        const timeSinceLastTap = now - lastTapTimeRef.current;

        if (
          timeSinceLastTap < DOUBLE_TAP_THRESHOLD_MS &&
          timeSinceLastTap > 0
        ) {
          e.preventDefault();
          e.stopPropagation();
          resetZoom();
          lastTapTimeRef.current = 0;
          return;
        }

        lastTapTimeRef.current = now;
      }

      // Handle pan (1 finger, only when zoomed)
      if (!isZoomed || e.touches.length !== 1) return;

      const touch = e.touches[0];
      if (!touch) return;

      e.preventDefault();
      e.stopPropagation();
      startDrag(touch.clientX, touch.clientY);
    };

    const handleTouchMove = (e: TouchEvent) => {
      const currentTouchCount = e.touches.length;

      // End pinch if going from 2 fingers to 1 finger
      if (previousTouchCount === 2 && currentTouchCount === 1) {
        endPinch();
      }

      previousTouchCount = currentTouchCount;

      // Handle pinch zoom
      if (e.touches.length === 2) {
        const touches = getTwoTouches(e.touches);
        if (!touches) return;

        e.preventDefault();
        e.stopPropagation();
        const distance = getDistance(touches.touch1, touches.touch2);
        updatePinch(distance);
        return;
      }

      // Handle pan - only prevent default when actually dragging and zoomed
      if (!isZoomed || !isDragging || e.touches.length !== 1) return;

      const touch = e.touches[0];
      if (!touch) return;

      e.preventDefault();
      e.stopPropagation();
      updateDrag(touch.clientX, touch.clientY);
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (e.touches.length === 0) {
        previousTouchCount = 0;
        endPinch();
        if (isZoomed && isDragging) {
          e.stopPropagation();
        }
        endDrag();
      }
    };

    // Add event listeners with passive: false to allow preventDefault
    container.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    container.addEventListener("touchend", handleTouchEnd);

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [
    isZoomed,
    isDragging,
    startDrag,
    updateDrag,
    endDrag,
    startPinch,
    updatePinch,
    endPinch,
    resetZoom,
  ]);

  return (
    <ZoomableImageContainer
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      style={inlineStyle}
    >
      <StyledZoomableImage
        src={src}
        alt={alt}
        $rotation={rotation}
        $zoomScale={zoomScale}
        $panX={panPosition.x}
        $panY={panPosition.y}
        fallback={fallback}
      />
      {showZoomControls && (
        <ZoomStaticButtonContainer>
          <ZoomControl
            currentZoom={zoomLevel}
            onZoomIn={zoomIn}
            onZoomOut={zoomOut}
            minZoom={minZoom}
            maxZoom={maxZoom}
          />
        </ZoomStaticButtonContainer>
      )}
    </ZoomableImageContainer>
  );
};
